import type { Language } from 'services/i18n';
import type { HttpUrl } from 'typings/http';

const ENGLISH_OG_IMAGE = {
  url: 'https://assets-cdn.wellhub.com/images/mep-cms/EN_UK_IT_meta_d8c2bf8ba9.png' as HttpUrl,
  altText:
    'corporate wellness for every employee, including fitness, nutrition and mindfulness',
};

const OG_IMAGE_BY_LANGUAGE: Record<
  Language,
  {
    url: HttpUrl;
    altText: string;
  }
> = {
  de: {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/DE_meta_fbbf18a719.png',
    altText: 'wohlbefinden am arbeitsplatz für alle mitarbeitenden',
  },
  'en-CA': ENGLISH_OG_IMAGE,
  'en-GB': ENGLISH_OG_IMAGE,
  'en-IE': ENGLISH_OG_IMAGE,
  'en-US': ENGLISH_OG_IMAGE,
  'es-AR': {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/AR_CL_MX_meta_6916751f30.png',
    altText: 'Bienestar en el trabajo para cada colaborador/a',
  },
  'es-CL': {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/AR_CL_MX_meta_6916751f30.png',
    altText: 'Bienestar en el trabajo para cada colaborador/a',
  },
  'es-ES': {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/ES_meta_f3f18d83d0.png',
    altText: 'Bienestar en el trabajo para cada empleado/a',
  },
  'es-MX': {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/AR_CL_MX_meta_6916751f30.png',
    altText: 'Bienestar en el trabajo para cada colaborador/a',
  },
  'fr-CA': {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/CA_FR_meta_8c82f58dca.png',
    altText: 'Gympass est maintenant wellhub',
  },
  it: {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/IT_meta_1733f287b3.png',
    altText: 'benessere aziendale per tutti i dipendenti',
  },
  'pt-BR': {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/BR_meta_550f3ba538.png',
    altText: 'bem-estar corporativo para todos',
  },
  'pt-PT': {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/PT_PT_meta_c7d419dee5.png',
    altText: 'gympass agora é wellhub',
  },
  // fallbacks
  en: ENGLISH_OG_IMAGE,
  es: {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/ES_meta_f3f18d83d0.png',
    altText: 'Bienestar en el trabajo para cada empleado/a',
  },
  pt: {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/BR_meta_550f3ba538.png',
    altText: 'bem-estar corporativo para todos',
  },
  ro: {
    url: 'https://assets-cdn.wellhub.com/images/mep-cms/RO_meta_image_8ac9c42e4f.png',
    altText: 'Starea de bine de zi cu zi pentru fiecare angajat',
  },
  // Benelux+France expansion (PAREXP-1164): no dedicated OG art yet, so these
  // share the generic English image like the other en-* fallbacks above.
  'de-AT': ENGLISH_OG_IMAGE,
  'de-CH': ENGLISH_OG_IMAGE,
  'fr-CH': ENGLISH_OG_IMAGE,
  'nl-NL': ENGLISH_OG_IMAGE,
  'fr-LU': ENGLISH_OG_IMAGE,
  'de-LU': ENGLISH_OG_IMAGE,
  'fr-FR': ENGLISH_OG_IMAGE,
  'nl-BE': ENGLISH_OG_IMAGE,
  'fr-BE': ENGLISH_OG_IMAGE,
  fr: ENGLISH_OG_IMAGE,
};

/**
 * Options for building meta tags for SEO purposes.
 * @interface MetaTagsOptions
 * @property {string} title - The page title for the meta tag and SEO.
 * @property {string} [description] - The page description for the meta tag.
 * @property {Language} [language] - The language of the page content.
 * @property {string} [keywords] - Comma-separated keywords for SEO purposes.
 * @property {string} url - The canonical URL of the page.
 */
interface MetaTagsOptions {
  title: string;
  description: string;
  language: Language;
  keywords: string;
  url?: HttpUrl;
}

/**
 * Builds an array of meta tags for SEO purposes.
 *
 * @param options - The meta tag configuration object
 * @param options.title - The page title (required)
 * @param options.description - The page description for meta tags
 * @param options.keywords - Comma-separated keywords for SEO
 * @param options.image - The image URL for social media previews
 * @param options.url - The page URL (required)
 *
 * @returns An array of meta tag objects with name and content properties,
 *          including Open Graph and Twitter Card meta tags
 *
 * @example
 * const tags = buildMetaTags({
 *   title: 'My Page',
 *   description: 'Page description',
 *   keywords: 'keyword1, keyword2',
 *   image: 'https://example.com/image.png',
 *   url: 'https://example.com/page'
 * });
 */
export function buildMetaTags({
  title,
  description,
  keywords,
  language,
  url,
}: MetaTagsOptions) {
  const ogImage =
    OG_IMAGE_BY_LANGUAGE[language] || OG_IMAGE_BY_LANGUAGE['en-US'];

  const tags = [
    { title },
    { name: 'description', content: description },
    { name: 'keywords', content: keywords },
    { name: 'og:locale', content: language },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:creator', content: '@getwellhub' },
    { name: 'twitter:site', content: '@getwellhub' },
    { name: 'og:type', content: 'website' },
    { name: 'og:title', content: title },
    { name: 'og:description', content: description },
    { name: 'twitter:image', content: ogImage.url },
    { name: 'twitter:image:alt', content: ogImage.altText },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'og:image', content: ogImage.url },
    { name: 'og:image:alt', content: ogImage.altText },
    ...(url
      ? [
          { name: 'og:url', content: url },
          { name: 'twitter:url', content: url },
        ]
      : []),
  ];

  return tags;
}
